// Individual brochure page components, keyed by `kind`. The layout planner
// (planPages.js) decides which pages to emit; the renderer maps kind -> component.
// Add a new page kind = add a component here + a case in planPages.

import { motion } from "framer-motion";
import { createContext, useContext, useEffect, useMemo, useState } from "react";
import { formatDateRange, formatLocation } from "../../lib/format.js";
import { subscribeToEvent, submitEventRegistration } from "../../firebase/db.js";
import { uploadRegistrationImage, urlFor } from "../../firebase/storage.js";
import { normalizeRegistrationFields } from "../registration/formConfig.js";

const rise = {
  hidden: { opacity: 0, y: 28 },
  show: { opacity: 1, y: 0, transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] } },
};

// The brochure scrolls inside its own container (.brochure: height:100dvh;
// overflow-y:auto), NOT the window. framer-motion's whileInView observes the
// browser viewport by default, so pages below the fold never register as
// "in view" and stay at opacity:0 — making the page look stuck on slide 1 with
// no scroll. We pass the scroll container as the IntersectionObserver root so
// reveals fire as you scroll inside it.
export const ScrollRootContext = createContext(null);

function Page({ children, hint }) {
  const rootRef = useContext(ScrollRootContext);
  return (
    <section className="bpage">
      <motion.div
        className="bpage-inner"
        variants={rise}
        initial="hidden"
        whileInView="show"
        viewport={{ once: true, amount: 0.3, root: rootRef || undefined }}
      >
        {children}
      </motion.div>
      {hint && <div className="scroll-hint">{hint}</div>}
    </section>
  );
}

export function CoverPage({ details, isFirst, ctaLabel = "Register →" }) {
  const when = formatDateRange(details.startDate, details.endDate);
  const where = formatLocation(details);
  const resolvedCtaLabel = details.registrationCtaLabel || ctaLabel;
  return (
    <Page hint={isFirst ? "Scroll ↓" : null}>
      <div className={details.heroImage ? "hero-cover" : "bcard"}>
        {details.heroImage && <img className="hero-image" src={urlFor(details.heroImage)} alt="" />}
        <div className={details.heroImage ? "hero-copy" : ""}>
          {details.kicker && <div className="kicker">{details.kicker}</div>}
          <h1 className="btitle">{details.title || "Your event title"}</h1>
          {details.subtitle && <p className="bsubtitle">{details.subtitle}</p>}
          <div className="bmeta">
            {when && (
              <div>
                <div className="label">When</div>
                <div className="value">{when}</div>
              </div>
            )}
            {where && (
              <div>
                <div className="label">Where</div>
                <div className="value">
                  {details.mapsUrl ? (
                    <a className="meta-link" href={details.mapsUrl} target="_blank" rel="noreferrer">
                      {where}
                      <span className="meta-link-hint">View on map</span>
                    </a>
                  ) : (
                    where
                  )}
                </div>
              </div>
            )}
          </div>
          {details.registrationUrl && (
            <a className="cta" href={details.registrationUrl} target="_blank" rel="noreferrer">
              {resolvedCtaLabel}
            </a>
          )}
        </div>
      </div>
    </Page>
  );
}

export function DetailsPage({ details, heading = "About" }) {
  return (
    <Page>
      <div className="bcard">
        <h2 className="section-title">{heading}</h2>
        <p style={{ fontSize: "1.1rem", lineHeight: 1.6, color: "var(--muted)" }}>
          {details.description}
        </p>
        {(details.cmeCredits || details.accreditedBy) && (
          <div className="bmeta" style={{ marginTop: 28 }}>
            {details.cmeCredits && (
              <div>
                <div className="label">Credits</div>
                <div className="value">{details.cmeCredits}</div>
              </div>
            )}
            {details.accreditedBy && (
              <div>
                <div className="label">Accredited by</div>
                <div className="value">{details.accreditedBy}</div>
              </div>
            )}
          </div>
        )}
      </div>
    </Page>
  );
}

export function TopicsPage({ details, heading = "Topics" }) {
  return (
    <Page>
      <div>
        <h2 className="section-title">{heading}</h2>
        <div className="page-scroll-region chips">
          {(details.topics || []).map((t, i) => (
            <span className="chip" key={i}>{t}</span>
          ))}
        </div>
      </div>
    </Page>
  );
}

export function SpeakersPage({ details, heading = "Speakers" }) {
  const initials = (name = "") =>
    name.split(" ").map((p) => p[0]).slice(0, 2).join("").toUpperCase();
  return (
    <Page>
      <div>
        <h2 className="section-title">{heading}</h2>
        <div className="page-scroll-region speakers">
          {(details.speakers || []).map((s, i) => (
            <div className="speaker" key={i}>
              <div className="avatar">{initials(s.name)}</div>
              <div>
                <div className="name">{s.name}</div>
                <div className="role">{[s.title, s.org].filter(Boolean).join(" · ")}</div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </Page>
  );
}

function timeRange(item) {
  const range = [item.startTime, item.endTime].filter(Boolean).join(" - ");
  if (range && item.duration) return `${range} (${item.duration})`;
  return range || item.duration || "";
}

function AgendaPage({ title, items = [] }) {
  // Collect unique day labels in insertion order
  const days = useMemo(() => {
    const seen = new Set();
    const out = [];
    items.forEach((item) => {
      const label = item.day || item.exactDate || null;
      if (label && !seen.has(label)) { seen.add(label); out.push(label); }
    });
    return out;
  }, [items]);

  const [activeDay, setActiveDay] = useState(null);

  // Reset when items change
  useEffect(() => { setActiveDay(null); }, [days.join(",")]);

  const visibleItems = activeDay
    ? items.filter((item) => (item.day || item.exactDate) === activeDay)
    : items;

  return (
    <Page>
      <div>
        <h2 className="section-title">{title}</h2>

        {days.length > 1 && (
          <div className="day-tabs">
            <button
              className={`day-tab${!activeDay ? " active" : ""}`}
              onClick={() => setActiveDay(null)}
            >
              All
            </button>
            {days.map((day) => (
              <button
                key={day}
                className={`day-tab${activeDay === day ? " active" : ""}`}
                onClick={() => setActiveDay(day)}
              >
                {day}
              </button>
            ))}
          </div>
        )}

        <div className="page-scroll-region agenda-list">
          {visibleItems.map((item, i) => (
            <article className="agenda-item" key={i}>
              <div className="agenda-no">{item.serial || i + 1}</div>
              <div>
                <div className="agenda-topline">
                  {item.day && <span>{item.day}</span>}
                  {item.exactDate && <span>{item.exactDate}</span>}
                  {timeRange(item) && <span>{timeRange(item)}</span>}
                </div>
                <h3>{item.topic || "Untitled topic"}</h3>
                {item.description && <p>{item.description}</p>}
                {(item.speaker || item.speakerInfo) && (
                  <div className="agenda-speaker">
                    {item.speaker && <strong>{item.speaker}</strong>}
                    {item.speakerInfo && <span>{item.speakerInfo}</span>}
                  </div>
                )}
              </div>
            </article>
          ))}
        </div>
      </div>
    </Page>
  );
}

export function SchedulePage({ details, title = "Daywise Schedule" }) {
  return <AgendaPage title={title} items={details.schedule || []} />;
}

export function WorkshopsPage({ details, title = "Workshops" }) {
  return <AgendaPage title={title} items={details.workshops || []} />;
}


export function CustomSlidePage({ slide }) {
  return (
    <Page>
      <div className={`custom-slide ${slide.image ? "has-image" : ""}`}>
        {slide.image && <img src={urlFor(slide.image)} alt="" />}
        <div className="custom-slide-copy">
          {slide.title && <h2 className="section-title">{slide.title}</h2>}
          {slide.body && <p>{slide.body}</p>}
        </div>
      </div>
    </Page>
  );
}

export function SubscribePage({ details, slug }) {
  const [form, setForm] = useState({ mobile: "", email: "" });
  const [status, setStatus] = useState("idle");

  async function submit(e) {
    e.preventDefault();
    if (!form.mobile.trim() && !form.email.trim()) {
      setStatus("invalid");
      return;
    }
    setStatus("saving");
    try {
      await subscribeToEvent(slug, form);
      setStatus("saved");
      setForm({ mobile: "", email: "" });
    } catch {
      setStatus("error");
    }
  }

  return (
    <Page>
      <div className="bcard">
        <h2 className="section-title">Subscribe for updates</h2>
        <p className="muted invite-copy">
          Leave your mobile number or email to get updates about {details.title || "this event"}.
        </p>
        <form className="subscribe-form" onSubmit={submit}>
          <input
            type="tel"
            placeholder="Mobile number"
            value={form.mobile}
            onChange={(e) => setForm({ ...form, mobile: e.target.value })}
          />
          <input
            type="email"
            placeholder="Email address"
            value={form.email}
            onChange={(e) => setForm({ ...form, email: e.target.value })}
          />
          <button className="cta" type="submit" disabled={status === "saving"}>{status === "saving" ? "Saving..." : "Subscribe"}</button>
        </form>
        {status === "invalid" && <p className="form-note error-note">Enter a mobile number or email address to subscribe.</p>}
        {status === "saved" && <p className="form-note">Thanks. You're subscribed for updates.</p>}
        {status === "error" && <p className="form-note error-note">Could not save right now. Please try again.</p>}
      </div>
    </Page>
  );
}

export function EventRegistrationPage({ details, slug }) {
  const fields = normalizeRegistrationFields(details.eventRegistrationFields || []);
  const initialState = Object.fromEntries(fields.map((field) => [field.id, ""]));
  const [form, setForm] = useState(initialState);
  const [uploading, setUploading] = useState({});
  const [status, setStatus] = useState("idle");

  useEffect(() => {
    setForm(initialState);
    setUploading({});
    setStatus("idle");
  }, [details.eventRegistrationFields]);

  async function onImageChange(field, file) {
    if (!file) return;
    setUploading((current) => ({ ...current, [field.id]: true }));
    try {
      const uploaded = await uploadRegistrationImage(slug, file);
      setForm((current) => ({ ...current, [field.id]: uploaded.path }));
      setStatus("idle");
    } catch {
      setStatus("error");
    } finally {
      setUploading((current) => ({ ...current, [field.id]: false }));
    }
  }

  async function submit(e) {
    e.preventDefault();
    const missing = fields.some((field) => field.required && !String(form[field.id] || "").trim());
    if (missing) {
      setStatus("invalid");
      return;
    }
    setStatus("saving");
    try {
      await submitEventRegistration(slug, form);
      setStatus("saved");
      setForm(Object.fromEntries(fields.map((field) => [field.id, ""])));
    } catch {
      setStatus("error");
    }
  }

  return (
    <Page>
      <div className="bcard">
        <h2 className="section-title">Event registration</h2>
        <p className="muted invite-copy">
          Submit your registration directly here for {details.title || "this event"}.
        </p>
        {details.registrationUrl && (
          <a className="cta secondary" href={details.registrationUrl} target="_blank" rel="noreferrer">
            Open external registration link
          </a>
        )}
        <form className="subscribe-form" onSubmit={submit}>
          <div className="page-scroll-region">
            {fields.map((field) => (
              <label key={field.id} className="registration-form-field">
                <span>{field.label}{field.required ? " *" : ""}</span>
                {field.kind === "image" ? (
                  <div className="stack" style={{ gap: 8 }}>
                    <input type="file" accept="image/*" onChange={(e) => onImageChange(field, e.target.files?.[0])} />
                    {uploading[field.id] && <small className="muted">Uploading image…</small>}
                    {!!form[field.id] && <img className="registration-upload-preview" src={urlFor(form[field.id])} alt={field.label} />}
                  </div>
                ) : (
                  <input
                    type={field.kind === "email" ? "email" : field.kind === "phone" ? "tel" : "text"}
                    inputMode={field.kind === "phone" ? "numeric" : undefined}
                    placeholder={field.label}
                    value={form[field.id] || ""}
                    onChange={(e) => setForm((current) => ({ ...current, [field.id]: e.target.value }))}
                  />
                )}
              </label>
            ))}
          </div>
          <button className="cta" type="submit" disabled={status === "saving" || Object.values(uploading).some(Boolean)}>
            {status === "saving" ? "Submitting..." : "Submit registration"}
          </button>
        </form>
        {status === "invalid" && <p className="form-note error-note">Please complete all required fields before submitting.</p>}
        {status === "saved" && <p className="form-note">Thanks. Your registration has been received.</p>}
        {status === "error" && <p className="form-note error-note">Could not save right now. Please try again.</p>}
      </div>
    </Page>
  );
}

export function ImagePage({ url, alt }) {
  return (
    <Page>
      <img className="bimg" src={url} alt={alt || "Invite page"} loading="lazy" />
    </Page>
  );
}

export function ClosingPage({ details, heading, ctaLabel = "Register →" }) {
  const resolvedCtaLabel = details.registrationCtaLabel || ctaLabel;
  const contacts = (details.contacts || []).filter((contact) =>
    contact.name || contact.designation || contact.mobile || contact.email
  );
  const legacyContact = !contacts.length && (details.contactName || details.contactMobile || details.contactDesignation || details.contactEmail)
    ? [{
        name: details.contactName,
        designation: details.contactDesignation,
        mobile: details.contactMobile,
        email: details.contactEmail,
      }]
    : [];
  const visibleContacts = contacts.length ? contacts : legacyContact;
  return (
    <Page>
      <div className="bcard center">
        <h2 className="section-title">{heading || details.title || "See you there"}</h2>
        {!!visibleContacts.length && (
          <div className="page-scroll-region contact-list">
            {visibleContacts.map((contact, i) => (
              <div className="contact-block" key={i}>
                {contact.name && <strong>{contact.name}</strong>}
                {contact.designation && <span>{contact.designation}</span>}
                {contact.mobile && <a href={`tel:${contact.mobile}`}>{contact.mobile}</a>}
                {contact.email && <a href={`mailto:${contact.email}`}>{contact.email}</a>}
              </div>
            ))}
          </div>
        )}
        {details.registrationUrl && (
          <a className="cta" href={details.registrationUrl} target="_blank" rel="noreferrer">
            {resolvedCtaLabel}
          </a>
        )}
        <div className="bfooter">Made with einvite.to</div>
      </div>
    </Page>
  );
}

export const PAGE_COMPONENTS = {
  cover: CoverPage,
  details: DetailsPage,
  topics: TopicsPage,
  speakers: SpeakersPage,
  schedule: SchedulePage,
  workshops: WorkshopsPage,
  customSlide: CustomSlidePage,
  eventRegistration: EventRegistrationPage,
  subscribe: SubscribePage,
  image: ImagePage,
  closing: ClosingPage,
};
